import { Controller } from "../vendor/stimulus.js";

const HEIGHT_EPSILON = 2; // Allow small rounding tolerance before growing

export default class extends Controller {
  connect() {
    this.handleInput = () => this.adjustHeight();
    this.handleResize = () => this.adjustHeight();
    this.handleObservedResize = () => this.adjustHeight();

    this.refreshMeasurements(true);
    this.element.addEventListener("input", this.handleInput);
    window.addEventListener("resize", this.handleResize);
    this.observeElement();
    this.adjustHeight();
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleInput);
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  refreshMeasurements(force = false) {
    const computed = window.getComputedStyle(this.element);
    const signature = [
      computed.fontSize,
      computed.lineHeight,
      computed.paddingTop,
      computed.paddingBottom,
      computed.borderTopWidth,
      computed.borderBottomWidth,
      computed.minHeight,
      computed.height,
      this.element.getAttribute("rows"),
      this.element.getAttribute("max-rows"),
    ].join("|");

    if (!force && signature === this.measurementSignature) {
      return;
    }

    this.measurementSignature = signature;
    this.lineHeight = this.resolveLineHeight(computed);
    this.extrasHeight =
      this.parseNumber(computed.paddingTop) +
      this.parseNumber(computed.paddingBottom) +
      this.parseNumber(computed.borderTopWidth) +
      this.parseNumber(computed.borderBottomWidth);

    const rowsAttr = parseInt(this.element.getAttribute("rows"), 10);
    if (Number.isFinite(rowsAttr)) {
      this.minHeight = (rowsAttr * this.lineHeight) + this.extrasHeight;
    } else {
      this.minHeight = this.resolveMinHeight(computed);
    }

    const maxRowsAttr = parseInt(this.element.getAttribute("max-rows"), 10);
    this.maxHeight = Number.isFinite(maxRowsAttr)
      ? (maxRowsAttr * this.lineHeight) + this.extrasHeight
      : null;

    this.element.style.overflowY = "hidden";
  }

  adjustHeight() {
    this.refreshMeasurements();
    const element = this.element;
    element.style.height = "auto";

    let newHeight = Math.max(element.scrollHeight, this.minHeight || 0);

    if (this.maxHeight) {
      const cappedHeight = Math.min(newHeight, this.maxHeight);
      element.style.overflowY = newHeight > this.maxHeight ? "auto" : "hidden";
      newHeight = cappedHeight;
    } else {
      element.style.overflowY = "hidden";
    }

    if (this.minHeight && (newHeight - this.minHeight) <= HEIGHT_EPSILON) {
      newHeight = this.minHeight;
    }

    element.style.height = `${newHeight}px`;
  }

  observeElement() {
    if (typeof ResizeObserver !== "function") return;
    this.resizeObserver = new ResizeObserver(() => this.handleObservedResize());
    this.resizeObserver.observe(this.element);
  }

  parseNumber(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  resolveMinHeight(computed) {
    const minHeight = this.parseNumber(computed.minHeight);
    if (minHeight > 0) return minHeight;

    const height = this.parseNumber(computed.height);
    if (height > 0) return height;

    return this.element.clientHeight || 0;
  }

  resolveLineHeight(computed) {
    const parsedLineHeight = this.parseNumber(computed.lineHeight);
    if (parsedLineHeight > 0) return parsedLineHeight;

    const probe = document.createElement("span");
    probe.textContent = "M";
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.whiteSpace = "pre";
    probe.style.font = computed.font || `${computed.fontSize} ${computed.fontFamily}`;
    document.body.appendChild(probe);
    const measured = probe.getBoundingClientRect().height;
    document.body.removeChild(probe);
    return measured || 16;
  }
}
